import { z } from 'zod'

import {
  ClassificationFacts,
  COMPONENT_TYPES,
  REGION_KINDS,
  SHELL_GEOMETRIES,
} from './ffsComponentClassification'
import { FfsPart } from './ffsTypes'
import { PIPING_MATERIAL_GROUPS } from './pipingMaterialGroups'

// --- Shared building blocks -------------------------------------------------

const CorrosionTypeSchema = z.enum(['local', 'general', 'groove-like'])
// Component type is owned by the classifier module (single source of truth);
// the Zod enum is built from that const so the two can never drift.
const ComponentTypeSchema = z.enum(COMPONENT_TYPES)

/** The one part+level actually executed for an LTA (Rules 4–9). */
export const ENTRY_ROUTES = [
  'PART_4_LEVEL_1',
  'PART_4_LEVEL_2',
  'PART_5_LEVEL_1',
  'PART_5_LEVEL_2',
  'NEITHER_APPLICABLE',
  'GROOVE_NOT_COVERED',
] as const
export type EntryRoute = (typeof ENTRY_ROUTES)[number]
const EntryRouteSchema = z.enum(ENTRY_ROUTES)

/** Local metal loss requires thickness profiles before any assessment. */
export const ProfileRequirementSchema = z.enum(['NOT_REQUIRED', 'REQUIRED'])
export type ProfileRequirement = z.infer<typeof ProfileRequirementSchema>

const LevelEligibilitySchema = z.object({
  // `eligible` = may this level be run at all (drives gating / tab-unlock).
  // `recommended` = do we proactively propose running it (suggestion display).
  eligible: z.boolean(),
  recommended: z.boolean(),
  reason: z.string().optional(),
})

const PartLevelEligibilitySchema = z.object({
  [FfsPart.PART_4]: z.object({
    level1: LevelEligibilitySchema,
    level2: LevelEligibilitySchema,
  }),
  [FfsPart.PART_5]: z.object({
    level1: LevelEligibilitySchema,
    level2: LevelEligibilitySchema,
  }),
})

export type CorrosionType = z.infer<typeof CorrosionTypeSchema>
// `ComponentType` is owned by (and imported from) `ffsComponentClassification`.
/**
 * The part/level eligibility matrix. Owned HERE, by the persisted schema, and
 * imported by `ffsRegionRouting` so that `componentTypeEligibility` — the
 * function that derives it — returns exactly the shape that gets stored. The two
 * therefore cannot drift structurally, on top of the runtime drift guards in
 * `validateFfsRegionClassification`.
 */
export type FfsPartLevelEligibility = z.infer<typeof PartLevelEligibilitySchema>

// --- Classification facts (Zod mirror of the classifier's ClassificationFacts) ---

const ClassificationFactsSchema = z
  .object({
    region: z.enum(REGION_KINDS).optional(),
    headIs2to1Elliptical: z.boolean().optional(),
    onCylinderSide: z.boolean().optional(),
    pressureOnlyLoading: z.boolean().optional(),
    stiffenerRatio: z.number().finite().optional(),
    shellGeometry: z.enum(SHELL_GEOMETRIES).optional(),
    Lss: z.number().finite().optional(),
    H: z.number().finite().optional(),
    D: z.number().finite().optional(),
    materialGroup: z.enum(PIPING_MATERIAL_GROUPS).optional(),
    nps: z.number().finite().optional(),
    designTempC: z.number().finite().optional(),
    pressureGovernsThickness: z.boolean().optional(),
    supplementalLoadsGovern: z.boolean().optional(),
    hasCodeDesignEquation: z.boolean().optional(),
    codeAssessmentProcedureAvailable: z.boolean().optional(),
  })
  .strict() satisfies z.ZodType<ClassificationFacts>

// --- Per-LTA classification record (persisted on the indication) ------------

/**
 * The committed per-region classification, stored on an LTA indication's
 * dedicated `ffsRegionClassification` field.
 *
 * Cross-field invariants (the facts classify to `componentType`, the stored
 * `eligibility` equals the canonical matrix, `profileRequirement` matches the
 * corrosion type, the (type, entryRoute) tuple is eligible, a Part-5 route has a
 * verified profile) are enforced by the DOMAIN validator in `ffsRegionRouting.ts`,
 * not here — keeping this module free of a cycle back to the classifier logic.
 */
export const FfsRegionClassificationSchema = z.object({
  componentType: ComponentTypeSchema,
  corrosionType: CorrosionTypeSchema,
  entryRoute: EntryRouteSchema,
  /**
   * `eligibility` and `profileRequirement` are DERIVED — `eligibility` from
   * `componentType` (via `componentTypeEligibility`, plus `recommended` marking
   * the `entryRoute`'s level) and `profileRequirement` from `corrosionType` (via
   * `profileRequirement`). They are persisted anyway, ON PURPOSE.
   *
   * WHY THEY ARE STORED: this record is an EXTERNAL READ SURFACE. Services and
   * reports outside this workflow read `customInfo.ffsRegionClassification` as
   * raw JSON, and the derivation logic — the API 579 eligibility matrix and the
   * profile rule — exists only in this frontend's TypeScript. Dropping these
   * fields would force every such consumer to reimplement that matrix. Storing
   * them keeps the record self-describing: an external reader learns which
   * part/level assessments are permitted, which one is recommended, and whether
   * thickness profiles are required, without any Gecko frontend code.
   *
   * They are NOT for in-repo use. Nothing in this repo reads the stored copies —
   * `ffsLtaDecision` and `FfsClassificationReference` call
   * `componentTypeEligibility` directly, which is correct and should stay that
   * way. The two can't disagree because `validateFfsRegionClassification` guards
   * the stored values against their derivations on both read and write.
   *
   * Do not "simplify" these away as redundant: they were removed exactly once, in
   * d979a73d9f, on the (repo-local, and therefore incomplete) reasoning that
   * nothing reads them, and restored by review on PR #8721. The consumer is
   * out-of-repo, so grep cannot see it.
   */
  eligibility: PartLevelEligibilitySchema,
  profileRequirement: ProfileRequirementSchema,
  classificationFacts: ClassificationFactsSchema,
  decidedBy: z.string(),
  decidedAt: z.string(),
})

export type FfsRegionClassification = z.infer<
  typeof FfsRegionClassificationSchema
>
